import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  getSettings,
  getStatus,
  listConfigVersions,
  onlineStorePreferencesDeepLink,
  publishConfig,
  restoreConfigVersion,
  rotateStaffLink,
  toastPublishResult,
  updateSettings,
} from "../lib/safegeo-api";
import { hasFeature, isFreePlan, planLabel } from "../lib/format";
import { fieldValue } from "../lib/polaris";
import { queryKeys } from "../lib/query-keys";
import { SafePage } from "../components/SafePage";
import { SaveBarHost } from "../components/SaveBarHost";
import { ErrorBanner } from "../components/PageBanner";
import { AppButton } from "../components/AppButton";
import { AppLink } from "../components/AppLink";
import { UpgradeNowButton } from "../components/UpgradeNowButton";
import { useSaveBar } from "../lib/save-bar";
import { showToast } from "../lib/feedback";

const SAVE_BAR_ID = "safegeo-settings-save";

/** v6 §26: General · Visitors and protection · History and alerts */
type SettingsTab = "general" | "visitors" | "alerts";

/** Usage alerts: Standard (80%) · Early (50%) · Only at the limit */
type UsageAlertMode = "standard" | "early" | "limit";

function parseTab(raw: string | null): SettingsTab {
  if (
    raw === "visitors" ||
    raw === "visitor" ||
    raw === "protect" ||
    raw === "protection"
  ) {
    return "visitors";
  }
  if (raw === "alerts" || raw === "account" || raw === "history")
    return "alerts";
  return "general";
}

function usageFromSettings(
  nearCap: boolean,
  pct: 50 | 80,
): UsageAlertMode {
  if (!nearCap) return "limit";
  return pct === 50 ? "early" : "standard";
}

export function SettingsPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = parseTab(params.get("tab"));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [staffCopied, setStaffCopied] = useState(false);
  const [accountIdCopied, setAccountIdCopied] = useState(false);
  const [baseline, setBaseline] = useState<string | null>(null);
  /** Respect choice (remember) vs always follow location (cookie_days 0). */
  const [respectChoice, setRespectChoice] = useState(true);

  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const settingsQ = useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
  });
  const free = isFreePlan(statusQ.data?.plan, statusQ.data?.plan_key);
  const versionsQ = useQuery({
    queryKey: ["safegeo", "versions"],
    queryFn: listConfigVersions,
    enabled: !free,
  });

  const [adsSafe, setAdsSafe] = useState(true);
  const [euSafe, setEuSafe] = useState(true);
  const [cookieDays, setCookieDays] = useState<7 | 30 | 60>(30);
  const [excluded, setExcluded] = useState("");
  const [usageAlerts, setUsageAlerts] = useState<UsageAlertMode>("standard");
  const [alertJoin, setAlertJoin] = useState(true);
  const [alertAlsoTo, setAlertAlsoTo] = useState("");

  useEffect(() => {
    const s = settingsQ.data;
    if (!s) return;
    setAdsSafe(s.ads_safe !== false);
    setEuSafe(s.eu_safe !== false);
    const days = Number(s.cookie_days);
    if (days === 0) {
      setRespectChoice(false);
      setCookieDays(30);
    } else {
      setRespectChoice(true);
      setCookieDays(days === 7 || days === 60 ? days : 30);
    }
    setExcluded((s.excluded_paths || []).join("\n"));
    const alerts = s.email_alerts;
    if (alerts) {
      setUsageAlerts(
        usageFromSettings(
          alerts.near_cap !== false,
          alerts.near_cap_pct === 50 ? 50 : 80,
        ),
      );
      setAlertJoin(alerts.join_request !== false);
    }
  }, [settingsQ.data]);

  const draftSnap = useMemo(
    () =>
      JSON.stringify({
        adsSafe,
        euSafe,
        respectChoice,
        cookieDays,
        excluded,
        usageAlerts,
        alertJoin,
        alertAlsoTo,
      }),
    [
      adsSafe,
      euSafe,
      respectChoice,
      cookieDays,
      excluded,
      usageAlerts,
      alertJoin,
      alertAlsoTo,
    ],
  );

  useEffect(() => {
    if (!settingsQ.data) return;
    if (baseline == null) setBaseline(draftSnap);
  }, [settingsQ.data, draftSnap, baseline]);

  const dirty = baseline != null && draftSnap !== baseline;
  useSaveBar(SAVE_BAR_ID, dirty);

  const status = statusQ.data;
  const settings = settingsQ.data;
  const planName = planLabel(status?.plan || settings?.plan);
  const featOpts = {
    plan: status?.plan,
    planKey: status?.plan_key,
  };
  const canToggleEu = hasFeature(
    status?.entitlements?.features,
    "euSafeToggleOff",
    featOpts,
  );
  const canAdsToggle = !free;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["safegeo"] });
  const prefsUrl = status?.shop_domain
    ? onlineStorePreferencesDeepLink(status.shop_domain)
    : null;

  function setTab(next: SettingsTab) {
    const n = new URLSearchParams(params);
    if (next === "general") n.delete("tab");
    else n.set("tab", next);
    setParams(n, { replace: true });
  }

  function discard() {
    const s = settingsQ.data;
    if (!s) return;
    setAdsSafe(s.ads_safe !== false);
    setEuSafe(s.eu_safe !== false);
    const days = Number(s.cookie_days);
    if (days === 0) {
      setRespectChoice(false);
      setCookieDays(30);
    } else {
      setRespectChoice(true);
      setCookieDays(days === 7 || days === 60 ? days : 30);
    }
    setExcluded((s.excluded_paths || []).join("\n"));
    const alerts = s.email_alerts;
    setUsageAlerts(
      usageFromSettings(
        alerts?.near_cap !== false,
        alerts?.near_cap_pct === 50 ? 50 : 80,
      ),
    );
    setAlertJoin(alerts?.join_request !== false);
    setAlertAlsoTo("");
    setErr(null);
    setBaseline(null);
  }

  async function onSave() {
    setBusy(true);
    setErr(null);
    try {
      const paths = excluded
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const nearCap = usageAlerts !== "limit";
      const nearCapPct: 50 | 80 = usageAlerts === "early" ? 50 : 80;
      await updateSettings({
        cookie_days: respectChoice ? cookieDays : 0,
        eu_safe: canToggleEu ? euSafe : true,
        ads_safe: canAdsToggle ? adsSafe : true,
        excluded_paths: paths,
        email_alerts: {
          // v6: embed-off alert always on
          embed_down: true,
          near_cap: nearCap,
          join_request: alertJoin,
          near_cap_pct: nearCapPct,
        },
      });
      let published = null;
      try {
        published = await publishConfig();
      } catch {
        /* settings saved; publish may fail */
      }
      const pub = toastPublishResult(
        published,
        "Saved and published to your storefront.",
      );
      if (!pub.ok && pub.message) setErr(pub.message);
      setBaseline(draftSnap);
      refresh();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  }

  async function setPaused(paused: boolean) {
    setBusy(true);
    setErr(null);
    try {
      await updateSettings({ paused });
      try {
        await publishConfig();
      } catch {
        /* ok */
      }
      showToast(paused ? "Switched to Test mode" : "Switched to Live");
      refresh();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  }

  async function copyStaffLink() {
    const link = settings?.staff_link;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setStaffCopied(true);
      showToast("Access link copied");
      window.setTimeout(() => setStaffCopied(false), 2000);
    } catch {
      showToast(link);
    }
  }

  async function copyAccountId() {
    if (!accountId) return;
    try {
      await navigator.clipboard.writeText(accountId);
      setAccountIdCopied(true);
      showToast("Store linking code copied");
      window.setTimeout(() => setAccountIdCopied(false), 2000);
    } catch {
      showToast(accountId);
    }
  }

  async function regenerateStaff() {
    setBusy(true);
    try {
      await rotateStaffLink();
      showToast("New access link ready. The old link no longer works.");
      refresh();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  }

  async function onRestore(versionId: string) {
    setBusy(true);
    setErr(null);
    try {
      await restoreConfigVersion(versionId);
      showToast("Restored previous settings");
      refresh();
      void qc.invalidateQueries({ queryKey: ["safegeo", "versions"] });
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusy(false);
    }
  }

  const isPaused = !!status?.settings?.paused;
  const storeLabel =
    status?.primary_domain ||
    status?.shop_domain ||
    settings?.contact_name ||
    "This store";
  const contactEmail = settings?.contact_email;
  const accountId =
    settings?.account_id ||
    (status?.shop_id
      ? `SG-${String(status.shop_id).replace(/-/g, "").slice(0, 7).toUpperCase()}`
      : null);

  const tabs: Array<{ id: SettingsTab; label: string }> = [
    { id: "general", label: "General" },
    { id: "visitors", label: "Visitors" },
    { id: "alerts", label: "History and alerts" },
  ];

  return (
    <SafePage screen="settings" heading="Settings">
      <SaveBarHost
        id={SAVE_BAR_ID}
        onSave={() => void onSave()}
        onDiscard={discard}
      />
      {err ? (
        <ErrorBanner heading="Could not save">{err}</ErrorBanner>
      ) : null}
      <s-banner tone="warning" heading="Shopify Automatic redirection">
        Make sure Shopify’s own Automatic redirection is off, so it doesn’t
        move visitors before SafeGeo runs.{" "}
        {prefsUrl ? (
          <AppLink href={prefsUrl}>How to check</AppLink>
        ) : (
          <AppLink to="/app/market">How to check</AppLink>
        )}
      </s-banner>

      <s-section>
        <s-stack direction="inline" gap="small">
          {tabs.map((t) => (
            <s-button
              key={t.id}
              type="button"
              variant={tab === t.id ? "primary" : "tertiary"}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </s-button>
          ))}
        </s-stack>
      </s-section>

      {tab === "general" ? (
        <s-section heading="General">
          <s-stack gap="base">
            <s-stack gap="small">
              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-text type="strong">Your store (from Shopify)</s-text>
                <s-badge>Synced from Shopify</s-badge>
              </s-stack>
              <s-paragraph color="subdued">
                Store:{" "}
                <s-text type="strong">{storeLabel}</s-text>
                {status?.shop_domain ? ` · ${status.shop_domain}` : ""}
              </s-paragraph>
              <s-paragraph color="subdued">
                Markets:{" "}
                {(status?.settings?.market_countries?.length ?? 0) || "—"}
                {(status?.settings?.market_countries?.length ?? 0) > 0
                  ? ` · ${(status?.settings?.market_countries || [])
                      .slice(0, 4)
                      .join(", ")}${
                      (status?.settings?.market_countries?.length || 0) > 4
                        ? "…"
                        : ""
                    }`
                  : ""}
              </s-paragraph>
              <s-paragraph color="subdued">
                Languages:{" "}
                {(() => {
                  const langs = status?.settings?.market_languages;
                  if (!langs || typeof langs !== "object") return "—";
                  const uniq = [
                    ...new Set(
                      Object.values(langs as Record<string, string>)
                        .flatMap((v) => String(v).split(/[,]/))
                        .map((s) => s.trim().toLowerCase())
                        .filter(Boolean),
                    ),
                  ];
                  return uniq.length ? uniq.join(", ") : "—";
                })()}
              </s-paragraph>
              <s-paragraph color="subdued">
                Time zone: UTC · monthly automatic-redirect limit resets on the
                1st (UTC).
              </s-paragraph>
              <s-paragraph color="subdued">
                Plan: <s-text type="strong">{planName}</s-text> · quota and
                billing on Home and Plan. Refresh never changes your Shopify
                settings.
              </s-paragraph>
              <s-stack direction="inline" gap="base">
                <AppButton variant="secondary" to="/app/market">
                  Refresh from Shopify
                </AppButton>
                <AppLink to="/app/billing">See plans</AppLink>
              </s-stack>
            </s-stack>

            <s-divider />

            <s-stack
              direction="inline"
              gap="base"
              alignItems="center"
              justifyContent="space-between"
            >
              <s-stack gap="small">
                <s-text type="strong">Test / Live</s-text>
                <s-paragraph color="subdued">
                  {isPaused
                    ? "Test mode - real visitors aren’t asked, redirected, or blocked. Your settings are kept."
                    : "Live - visitors see SafeGeo. Switch to Test to stop affecting real shoppers."}
                </s-paragraph>
              </s-stack>
              <s-stack direction="inline" gap="small">
                <s-button
                  type="button"
                  variant={isPaused ? "primary" : "secondary"}
                  disabled={busy}
                  onClick={() => void setPaused(true)}
                >
                  Test mode
                </s-button>
                <s-button
                  type="button"
                  variant={!isPaused ? "primary" : "secondary"}
                  disabled={busy}
                  onClick={() => void setPaused(false)}
                >
                  Live
                </s-button>
              </s-stack>
            </s-stack>

            <s-divider />

            <s-stack
              direction="inline"
              gap="base"
              alignItems="start"
              justifyContent="space-between"
            >
              <s-stack gap="small">
                <s-text type="strong">Staff access link</s-text>
                <s-paragraph color="subdued">
                  Open your store without being asked, redirected, or blocked.
                  Creating a new link stops the old one. Share only with your
                  team.
                </s-paragraph>
              </s-stack>
              <s-stack direction="inline" gap="small">
                <AppButton
                  variant="secondary"
                  disabled={!settings?.staff_link}
                  onClick={() => void copyStaffLink()}
                >
                  {staffCopied ? "Copied" : "Copy link"}
                </AppButton>
                <AppButton
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void regenerateStaff()}
                >
                  Create new link
                </AppButton>
              </s-stack>
            </s-stack>
          </s-stack>
        </s-section>
      ) : null}

      {tab === "visitors" ? (
        <s-stack gap="base">
          <s-section heading="Returning visitors">
            <s-stack gap="base">
              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-stack gap="small">
                  <s-text type="strong">Returning visitors</s-text>
                  <s-paragraph color="subdued">
                    {respectChoice
                      ? "Visitors who already answered aren’t asked again."
                      : "Visitors are matched to their location on every visit. Nothing is remembered between visits, though the same tab may still remember."}
                  </s-paragraph>
                </s-stack>
                <s-stack direction="inline" gap="small">
                  <s-button
                    type="button"
                    variant={respectChoice ? "primary" : "secondary"}
                    onClick={() => setRespectChoice(true)}
                  >
                    Respect their choice
                  </s-button>
                  <s-button
                    type="button"
                    variant={!respectChoice ? "primary" : "secondary"}
                    onClick={() => setRespectChoice(false)}
                  >
                    Always follow location
                  </s-button>
                </s-stack>
              </s-stack>

              {respectChoice ? (
                <>
                  <s-divider />
                  <s-stack
                    direction="inline"
                    gap="base"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <s-stack gap="small">
                      <s-text type="strong">Remember their choice for</s-text>
                      <s-paragraph color="subdued">
                        After a visitor chooses a country in the popup or
                        closes it with X, they aren’t asked again for this long.
                      </s-paragraph>
                    </s-stack>
                    <s-stack direction="inline" gap="small">
                      {([7, 30, 60] as const).map((d) => (
                        <s-button
                          key={d}
                          type="button"
                          variant={cookieDays === d ? "primary" : "secondary"}
                          onClick={() => setCookieDays(d)}
                        >
                          {d} days
                        </s-button>
                      ))}
                    </s-stack>
                  </s-stack>
                </>
              ) : null}
            </s-stack>
          </s-section>

          <s-section heading="Protection">
            <s-stack gap="base">
              <s-stack gap="small">
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-text type="strong">Search crawlers</s-text>
                  <s-badge>Always on</s-badge>
                </s-stack>
                <s-paragraph color="subdued">
                  Search bots are never redirected or blocked, so Google can
                  still index your store.
                </s-paragraph>
              </s-stack>

              <s-divider />

              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-stack gap="small">
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-text type="strong">Ask first on ad clicks</s-text>
                    <s-badge tone="success">Recommended</s-badge>
                  </s-stack>
                  <s-paragraph color="subdued">
                    If a visitor arrives from an ad, show the popup instead of
                    switching automatically. Helps protect paid traffic.
                  </s-paragraph>
                  {!canAdsToggle ? (
                    <s-paragraph color="subdued">
                      On and locked.{" "}
                      <AppLink to="/app/billing">Change on Starter</AppLink>
                    </s-paragraph>
                  ) : null}
                </s-stack>
                <s-stack direction="inline" gap="small">
                  <s-button
                    type="button"
                    variant={adsSafe ? "primary" : "secondary"}
                    disabled={!canAdsToggle}
                    onClick={() => setAdsSafe(true)}
                  >
                    On
                  </s-button>
                  <s-button
                    type="button"
                    variant={!adsSafe ? "primary" : "secondary"}
                    disabled={!canAdsToggle}
                    onClick={() => setAdsSafe(false)}
                  >
                    Off
                  </s-button>
                </s-stack>
              </s-stack>

              <s-divider />

              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-stack gap="small">
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-text type="strong">
                      Ask first for EU / EEA visitors
                    </s-text>
                    <s-badge tone="success">Recommended</s-badge>
                  </s-stack>
                  <s-paragraph color="subdued">
                    Recommended for GDPR-friendly consent before changing market
                    or currency.
                  </s-paragraph>
                  {!canToggleEu ? (
                    <s-paragraph color="subdued">
                      On and locked.{" "}
                      <AppLink to="/app/billing">Change on Starter</AppLink>
                    </s-paragraph>
                  ) : null}
                </s-stack>
                <s-stack direction="inline" gap="small">
                  <s-button
                    type="button"
                    variant={euSafe ? "primary" : "secondary"}
                    disabled={!canToggleEu}
                    onClick={() => setEuSafe(true)}
                  >
                    On
                  </s-button>
                  <s-button
                    type="button"
                    variant={!euSafe ? "primary" : "secondary"}
                    disabled={!canToggleEu}
                    onClick={() => setEuSafe(false)}
                  >
                    Off
                  </s-button>
                </s-stack>
              </s-stack>

              <s-divider />

              <s-stack
                direction="inline"
                gap="base"
                alignItems="start"
                justifyContent="space-between"
              >
                <s-stack gap="small">
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-text type="strong">
                      Pages where SafeGeo stays quiet
                    </s-text>
                    <s-badge tone="success">All plans</s-badge>
                  </s-stack>
                  <s-paragraph color="subdued">
                    One path per line, like /pages/wholesale. Cart, checkout,
                    and account are always skipped.
                  </s-paragraph>
                </s-stack>
                <s-box minInlineSize="280px">
                  <s-text-field
                    label="Excluded paths"
                    labelAccessibilityVisibility="exclusive"
                    value={excluded}
                    multiline={3}
                    onChange={(e: Event) => setExcluded(fieldValue(e))}
                  />
                </s-box>
              </s-stack>

              <s-divider />

              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-stack gap="small">
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-text type="strong">IP allow / block</s-text>
                    <s-badge>Starter+</s-badge>
                  </s-stack>
                  <s-paragraph color="subdued">
                    Always allow or always block specific IP addresses.
                  </s-paragraph>
                </s-stack>
                {free ? (
                  <UpgradeNowButton plan="starter" />
                ) : (
                  <s-paragraph color="subdued">
                    Manage IP rules from{" "}
                    <AppLink to="/app/block">Block country</AppLink> when adding
                    a country rule.
                  </s-paragraph>
                )}
              </s-stack>
            </s-stack>
          </s-section>
        </s-stack>
      ) : null}

      {tab === "alerts" ? (
        <s-stack gap="base">
          <s-section heading="Someone says they’re blocked?">
            <s-paragraph color="subdued">
              Look up the code on their screen (like SG-XXXX) on Block country.
            </s-paragraph>
            <AppButton variant="secondary" to="/app/block?lookup=1">
              Look up a code
            </AppButton>
          </s-section>

          <s-section
            heading="Recent decisions"
            subheading="Sample of what SafeGeo did for visitors. Full lookup uses the code on Block country."
          >
            <s-paragraph color="subdued">
              No recent decisions yet. After you’re Live, blocked and asked
              visitors show here. Codes are kept about 30 days.
            </s-paragraph>
          </s-section>

          <s-section heading="Configuration history">
            <s-stack gap="base">
              <s-badge>Starter+</s-badge>
              <s-paragraph color="subdued">
                Go back to an earlier version of your rules and popup if a
                change goes wrong.
              </s-paragraph>
              {free ? (
                <UpgradeNowButton plan="starter" />
              ) : versionsQ.isLoading ? (
                <s-paragraph color="subdued">Loading…</s-paragraph>
              ) : (versionsQ.data?.items?.length ?? 0) === 0 ? (
                <s-paragraph color="subdued">
                  No published versions yet. Save and publish to start a
                  history.
                </s-paragraph>
              ) : (
                <s-box borderWidth="base" borderRadius="base" padding="none">
                  <s-stack gap="none">
                    {(versionsQ.data?.items ?? []).slice(0, 8).map((v, i) => (
                      <s-box
                        key={v.id}
                        padding="base"
                        borderBlockStartWidth={i === 0 ? undefined : "base"}
                      >
                        <s-stack
                          direction="inline"
                          gap="base"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <s-stack gap="small">
                            <s-text type="strong">
                              {v.published_at
                                ? new Date(v.published_at).toLocaleString(
                                    "en-US",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    },
                                  )
                                : `Version ${v.version}`}
                            </s-text>
                            <s-paragraph color="subdued">
                              {v.reason || "Published settings"}
                              {v.current ? " · Current" : ""}
                            </s-paragraph>
                          </s-stack>
                          {!v.current ? (
                            <AppButton
                              variant="tertiary"
                              disabled={busy}
                              onClick={() => void onRestore(v.id)}
                            >
                              Restore
                            </AppButton>
                          ) : null}
                        </s-stack>
                      </s-box>
                    ))}
                  </s-stack>
                </s-box>
              )}
            </s-stack>
          </s-section>

          <s-section
            heading="Alerts"
            subheading="Email alerts go to your shop contact. Change the email in your Shopify profile."
          >
            <s-stack gap="base">
              <s-stack gap="small">
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-text type="strong">Theme or embed may be off</s-text>
                  <s-badge>Always on</s-badge>
                </s-stack>
                <s-paragraph color="subdued">
                  We always email when SafeGeo may have stopped running on the
                  storefront.
                </s-paragraph>
              </s-stack>

              <s-divider />

              <s-stack gap="small">
                <s-text type="strong">Usage alerts</s-text>
                <s-paragraph color="subdued">
                  How early to warn you about the monthly automatic redirect
                  limit (Free and Starter). 100% is always emailed.
                </s-paragraph>
                <s-stack direction="inline" gap="small">
                  {(
                    [
                      ["standard", "Standard"],
                      ["early", "Early"],
                      ["limit", "Only at the limit"],
                    ] as const
                  ).map(([id, label]) => (
                    <s-button
                      key={id}
                      type="button"
                      variant={usageAlerts === id ? "primary" : "secondary"}
                      onClick={() => setUsageAlerts(id)}
                    >
                      {label}
                    </s-button>
                  ))}
                </s-stack>
                <s-paragraph color="subdued">
                  {usageAlerts === "early"
                    ? "Email at 50% and 80%."
                    : usageAlerts === "limit"
                      ? "Email only when the limit is reached."
                      : "Email at 80% (recommended)."}
                </s-paragraph>
              </s-stack>

              <s-divider />

              <s-stack
                direction="inline"
                gap="base"
                alignItems="center"
                justifyContent="space-between"
              >
                <s-stack gap="small">
                  <s-text type="strong">Another store wants to join</s-text>
                  <s-paragraph color="subdued">
                    When a connected store asks for access (Multi-store and
                    Scale).
                  </s-paragraph>
                </s-stack>
                <s-stack direction="inline" gap="small">
                  <s-button
                    type="button"
                    variant={alertJoin ? "primary" : "secondary"}
                    onClick={() => setAlertJoin(true)}
                  >
                    On
                  </s-button>
                  <s-button
                    type="button"
                    variant={!alertJoin ? "primary" : "secondary"}
                    onClick={() => setAlertJoin(false)}
                  >
                    Off
                  </s-button>
                </s-stack>
              </s-stack>

              <s-text-field
                label="Also send alerts to"
                value={alertAlsoTo}
                onChange={(e: Event) => setAlertAlsoTo(fieldValue(e))}
                details="Optional extra email. Shop contact still receives alerts."
              />
              {contactEmail ? (
                <s-paragraph color="subdued">
                  Primary alerts go to {contactEmail} ({storeLabel}).
                </s-paragraph>
              ) : null}
            </s-stack>
          </s-section>

          {accountId ? (
            <s-section
              heading="Store linking code"
              subheading="Share when another of your shops joins (Multi-store / Scale), or when you email support."
            >
              <s-stack direction="inline" gap="base" alignItems="center">
                <s-text type="strong">{accountId}</s-text>
                <AppButton
                  variant="secondary"
                  onClick={() => void copyAccountId()}
                >
                  {accountIdCopied ? "Copied" : "Copy"}
                </AppButton>
              </s-stack>
            </s-section>
          ) : null}
        </s-stack>
      ) : null}
    </SafePage>
  );
}
